import { useDispatch, useSelector } from 'react-redux'

import { useAccessLevel } from '../../../store/user'
import { useFeature } from '../../../store/features'
import { useBotFlow } from '../../../store/flow'
import { DropdownMenuItem } from '../../design-system/DropdownMenu'
import { navigateToEnv } from '../../../features/serverBots/actions/navigateToEnv'
import { createDevBotForCurrentBot } from '../../../features/serverBots/actions/createDevBotForCurrentBot'
import { moveDevToProduction } from '../../../features/serverBots/actions/moveDevToProduction'
import { replaceDevWithProduction } from '../../../features/serverBots/actions/replaceDevWithProduction'
import { switchBot } from '../../../lib/navigation'
import { currentBotEnvironmentTypeSelector } from '../../../features/serverBots/selectors/currentBotEnvironmentTypeSelector'
import { currentBotEnvironmentGroupIdSelector } from '../../../features/serverBots/selectors/currentBotEnvironmentGroupIdSelector'
import { botMetadataForEnvironmentSelector } from '../../../features/serverBots/selectors/botMetadataForEnvironmentSelector'
import { endpointHelpers } from '../../../features/serverBots/endpointHelpers'
import { environmentGroupForCurrentBotSelector } from '@features/serverBots/selectors/environmentGroupForCurrentBotSelector'
import { createItemList } from '../../../lib/array'

export const itemDefaults = {
  type: 'text',
  label: 'label',
  description: 'description',
  isActive: false,
  className: 'class-name',
  textColor: '#354052'
} as const

export const useDropdownItems = () => {
  const dispatch = useDispatch()
  const accessLevel = useAccessLevel()
  const version = useBotFlow()?.version
  endpointHelpers.getBotsMetadata.useQuery()
  const isEnvironmentsEnabled = useFeature('ENVIRONMENTS')
  const currentBotEnv = useSelector(currentBotEnvironmentTypeSelector)
  const environmentGroupId = useSelector(currentBotEnvironmentGroupIdSelector)
  const devMetadata = useSelector(botMetadataForEnvironmentSelector(environmentGroupId, 'test'))
  const { prodId, prodName, testId, testName } = useSelector(environmentGroupForCurrentBotSelector)

  if (!environmentGroupId || !currentBotEnv || !isEnvironmentsEnabled) {
    return []
  }

  const isDev = currentBotEnv === 'test'
  const devExists = !!testId
  const prodExists = !!prodId
  const isDevEnabled = devMetadata?.environmentEnabledStatus

  return createItemList<DropdownMenuItem>([
    {
      ...itemDefaults,
      type: 'text',
      label: prodName,
      description: isDev ? '' : `v${version}`,
      svgIcon: isDev ? 'radioButton' : 'radioButtonSelected',
      onClick: isDev ? () => dispatch(navigateToEnv('prod')) : undefined,
      className: isDev ? 'icon' : 'activeIcon'
    },
    {
      ...itemDefaults,
      condition: devExists,
      type: 'text',
      label: testName,
      description: isDev ? `v${version}` : '',
      svgIcon: isDev ? 'radioButtonSelected' : 'radioButton',
      onClick: isDev ? undefined : () => dispatch(navigateToEnv('test')),
      className: isDev ? 'activeIcon' : 'icon',
      submenuItems: accessLevel.copywrite &&
        isDevEnabled && [
          {
            ...itemDefaults,
            type: 'text',
            label: `Replace with ${prodName}`,
            description: '',
            onClick: () => dispatch(replaceDevWithProduction())
          }
        ]
    },
    {
      ...itemDefaults,
      condition: isDev && prodExists && accessLevel.copywrite,
      type: 'button',
      label: `Copy to ${prodName}`,
      description: '',
      onClick: () => dispatch(moveDevToProduction())
    },
    {
      ...itemDefaults,
      condition: !devExists && accessLevel.admin,
      type: 'button',
      label: `Create ${testName}`,
      description: '',
      onClick: async () => {
        const newBotId: string | undefined = await dispatch(createDevBotForCurrentBot() as any)
        if (!newBotId) return
        switchBot(newBotId)
      }
    }
  ])
}
